import logger from '@finn-no/fiaas-logger';
import type { NextPage } from 'next';
import { useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { getPageCounter } from 'shared/prometheus/prometheus';
import prepackageFaq from 'shared/seo/faq/prepackageFaq.json';
import type { SspContext } from 'shared/types';
import { fetchHotel, fetchOffersForHotel } from '@/api/fetcher';
import { HotelPage } from '@/components/hotel/HotelPage';
import { config, feConfig } from '@/config';
import { useHotelSeo } from '@/hooks/useHotelSeo';
import type { Filters, HotelOffersResponse, PaleState } from '@/interfaces';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { runAllMiddlewares } from '@/server/middleware';
import { actions } from '@/state/filterActions';
import { actions as hotelActions } from '@/state/hotelActions';
import { actions as hotelResultActions } from '@/state/hotelResultActions';
import { wrapper } from '@/state/store';

const getPreferredSupplier = (hotelResults: HotelOffersResponse): string | undefined =>
    hotelResults.offers.length > 0 ? hotelResults.offers[0].supplier : undefined;

const buildBreadcrumbList = (hotelName: string, url: string) =>
    JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.finn.no/reise/', name: 'Reise' } },
            { '@type': 'ListItem', position: 2, item: { '@id': 'https://www.finn.no/reise/pakkereiser/', name: 'Pakkereiser' } },
            { '@type': 'ListItem', position: 3, item: { '@id': 'https://www.finn.no/reise/pakkereiser/resultat', name: 'Resultat' } },
            { '@type': 'ListItem', position: 4, item: { '@id': url, name: hotelName } },
        ],
    });

const NextHotelPage: NextPage = (props) => {
    wrapper.useHydration(props);
    const hotel = useSelector((state: PaleState) => state.hotel);

    const { title, description, url, image } = useHotelSeo(hotel);

    return (
        <>
            <SimpleHead title={title} description={description} canonical={url} imageUrl={image}>
                {hotel?.id && url && (
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: buildBreadcrumbList(hotel.name, url) }} />
                )}
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(prepackageFaq) }} />
            </SimpleHead>
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <HotelPage />
            </Main>
        </>
    );
};

export const getServerSideProps = wrapper.getServerSideProps((store) => async ({ req, res, query }: SspContext) => {
    await runAllMiddlewares(req, res, query);

    const filters: Partial<Filters> = browserQueryParamsToFilters(query);
    store.dispatch(actions.filter(filters));

    const apiQueryParams = filtersToApiQueryParams(filters);

    try {
        const offerData = await fetchOffersForHotel(config.get('pp.apiUrlInternal'), apiQueryParams);
        if (offerData) {
            store.dispatch(hotelResultActions.setResults(offerData));

            try {
                const preferredSupplier = getPreferredSupplier(offerData);
                const hotelData = await fetchHotel(config.get('pp.apiUrlInternal'), { ...apiQueryParams, preferredSupplier });
                store.dispatch(hotelActions.setHotel(hotelData));
            } catch (error) {
                // Hotel fetch failed, but offers succeeded
                logger.error(`Failed to fetch hotel data for hotel page: ${error}`);
            }
        } else {
            store.dispatch(hotelResultActions.setNotModified());
        }
    } catch (error) {
        store.dispatch(hotelResultActions.setError());
        logger.error(`Failed to fetch hotel offers for hotel page: ${error}`);
    }

    getPageCounter().inc({ app: 'pale-pp-web', page: 'hotell' });

    return {
        props: {
            hasLegalBasis: res.locals?.hasLegalBasis ?? false,
            unleash: res.locals?.unleash ?? null,
            config: feConfig,
        },
    };
});

export default NextHotelPage;
